import { useState, useCallback, useEffect } from "react";
import { Search, UserPlus, Check } from "lucide-react";
import {
  cn,
  Sheet,
  Button,
  ItemTable,
  ItemTableSearch,
  ItemTableBody,
  ItemTableRow,
  ItemTableCell,
  ItemTableAvatar,
  ItemTableTitle,
  ItemTableSubtitle,
  ItemTablePagination,
  ItemTableEmpty,
  ItemTableLoading,
} from "@ui";
import { useProfilesSearch } from "./use-profiles-search";
import { ProfileInviteSheet } from "./profile-invite-sheet";
import type { Profile, Invitee } from "./types";

interface ProfilePickerSheetProps {
  open: boolean;
  onClose: () => void;
  searchUrl: string;
  profileType: string;
  onSelect: (profile: Profile) => void;
  onInviteSent: (invitee: Invitee) => void;
  selectedIds: number[];
  allowInvite: boolean;
  label: string;
  sourceType?: string;
  sourceId?: number | string;
}

export function ProfilePickerSheet({
  open,
  onClose,
  searchUrl,
  profileType,
  onSelect,
  onInviteSent,
  selectedIds,
  allowInvite,
  label,
  sourceType,
  sourceId,
}: ProfilePickerSheetProps) {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [isInviteOpen, setIsInviteOpen] = useState(false);

  const { data, meta, isLoading } = useProfilesSearch(searchUrl, query, page);

  // Reset search when opening
  useEffect(() => {
    if (open) {
      setQuery("");
      setPage(1);
    }
  }, [open]);

  const handleRowClick = useCallback(
    (profile: Profile) => {
      if (!selectedIds.includes(profile.id)) {
        onSelect(profile);
      }
    },
    [selectedIds, onSelect],
  );

  const handleSearchChange = (value: string) => {
    setQuery(value);
    setPage(1);
  };

  return (
    <>
      <Sheet
        open={open}
        onClose={onClose}
        title={`Sélectionner ${label}`}
        width="lg"
        head={
          <ItemTableSearch
            value={query}
            onChange={handleSearchChange}
            isLoading={isLoading}
            placeholder="Rechercher..."
          />
        }
        footer={
          allowInvite && (
            <Button
              variant="white"
              icon={UserPlus}
              onClick={() => setIsInviteOpen(true)}
              className="w-full"
            >
              Profil introuvable ? Inviter
            </Button>
          )
        }
      >
        <ItemTable>
          {isLoading && data.length === 0 ? (
            <ItemTableLoading rows={5} />
          ) : data.length === 0 ? (
            <ItemTableEmpty
              icon={Search}
              title="Aucun résultat"
              description={
                query
                  ? `Aucun résultat pour "${query}"`
                  : "Commencez à taper pour rechercher"
              }
              action={
                allowInvite &&
                query && (
                  <Button icon={UserPlus} onClick={() => setIsInviteOpen(true)}>
                    Inviter
                  </Button>
                )
              }
            />
          ) : (
            <>
              <ItemTableBody>
                {data.map((profile) => {
                  const isSelected = selectedIds.includes(profile.id);
                  return (
                    <ItemTableRow
                      key={profile.id}
                      onClick={() => handleRowClick(profile)}
                      className={cn(
                        "grid-cols-[1fr_auto]",
                        isSelected && "bg-default-50",
                      )}
                    >
                      <ItemTableCell>
                        <ItemTableAvatar
                          src={profile.avatar_url}
                          alt={profile.name}
                        />
                        <div className="min-w-0">
                          <ItemTableTitle>{profile.name}</ItemTableTitle>
                          {profile.slug && (
                            <ItemTableSubtitle>
                              @{profile.slug}
                            </ItemTableSubtitle>
                          )}
                        </div>
                      </ItemTableCell>
                      <ItemTableCell>
                        {isSelected && (
                          <Check className="size-5 text-default-600" />
                        )}
                      </ItemTableCell>
                    </ItemTableRow>
                  );
                })}
              </ItemTableBody>

              {meta && meta.total_pages > 1 && (
                <ItemTablePagination meta={meta} onPageChange={setPage} />
              )}
            </>
          )}
        </ItemTable>
      </Sheet>

      {/* Invite sheet (Layer 3) */}
      {allowInvite && (
        <ProfileInviteSheet
          open={isInviteOpen}
          onClose={() => setIsInviteOpen(false)}
          profileType={profileType}
          prefillEmail={query.includes("@") ? query : undefined}
          sourceType={sourceType}
          sourceId={sourceId}
          onInviteSent={(invite) => {
            onInviteSent(invite);
            setIsInviteOpen(false);
          }}
        />
      )}
    </>
  );
}
